import React from 'react';
import styled from 'styled-components';
import { ifProp } from 'styled-tools';
import PropTypes from 'prop-types';
import { pure } from 'recompose';

import { Flex, Box, Column as RebassColumn } from 'rebass';

import { Row } from 'shared-components';

// import {
//   GuestListFooter,
// } from 'components';

import {
  GuestListEntry,
  GuestListHeading,
  SelectCabinModal,
  SelectAllDropdown,
  ExpandAllButton,
  Vehicle,
  T,
} from 'containers';

import { getGuests, getCabins, getVehicles } from 'helpers/reservation';
import { AppText } from 'domain/appText';

const ListHolder = styled(RebassColumn)`
  background-color: white;
  box-shadow: 0px 0px 1px 1px #dae6f2;
  padding: 0;
`;

const TableHeader = styled(Flex)`
  background-color: #dfe9f2;
  font-weight: bold;
  color: #17324c !important;

  &.sticky {
    position: fixed;
    top: 70px;
    margin-left: 0px;
    -webkit-transition: all 0.5s;
    transition: all 0.5s;
    z-index: 99;
    width: 100%;
    max-width: 1440px;
    min-width: 1100px;
  }
`;
const TableTitle = styled(Box)`
  color: #334d66;

  .Select-placeholder, .Select--single > .Select-control .Select-value{
    color: #334d66;
  }
`;
const TableTitleRight = styled(Box)`
  color: #334d66;
  button{
    span{
      line-height: 30px;
      color: #334d66;
    }
  }
`;
const PaddedTableTitle = styled(TableTitle)`
  padding-left: 20px;
  padding-top: 18px;
  padding-bottom: 18px;
  line-height: 13px;
`;
const StyledExpandAllButton = styled(ExpandAllButton)`
  border-width: 0px !important;
  background-color: transparent !important;
  width: 100%;
`;
const MySelectAllDropdown = props => <SelectAllDropdown verticalAlign arrowColor="#000" changeAll {...props} />;

const GuestListTableHeader = pure(({ widths, departureId, hideCabinLabel }) => (
  <T
    text={[
      AppText.FirstName,
      AppText.FirstNameDescription,
      AppText.LastName,
      AppText.LastNameDescription,
      AppText.DateOfBirth,
      AppText.DateOfBirthDescription,
      AppText.Gender,
      AppText.GenderDescription,
      AppText.Age,
      AppText.AgeDescription,
      AppText.Nationality,
      AppText.NationalityDescription,
      AppText.Cabin,
      AppText.CabinDescription,
      AppText.ExpandAllDescription,
    ]}
    render={([
      firstName,
      firstNameDescription,
      lastName,
      lastNameDescription,
      dateOfBirth,
      dateOfBirthDescription,
      gender,
      genderDescription,
      age,
      ageDescription,
      nationality,
      nationalityDescription,
      cabin,
      cabinDescription,
      expandAllDescription,
    ]) => (
      <TableHeader className="table-header" align="center" py={0}>
        <Box width={widths.number} />
        { widths.firstName === 0 ? null : <PaddedTableTitle px={1} width={widths.firstName} title={firstNameDescription}>{firstName}</PaddedTableTitle>}
        { widths.lastName === 0 ? null : <PaddedTableTitle px={1} width={widths.lastName} title={lastNameDescription}>{lastName}</PaddedTableTitle>}
        { widths.birthday === 0 ? null : <PaddedTableTitle px={1} width={widths.birthday} title={dateOfBirthDescription}>{dateOfBirth}</PaddedTableTitle>}
        { widths.gender === 0 ? null : <TableTitle my={-2} width={widths.gender} title={genderDescription}><MySelectAllDropdown nofade field="gender" defaultText={gender} /></TableTitle>}
        { widths.age === 0 ? null : <TableTitle my={-2} width={widths.age} title={ageDescription}><MySelectAllDropdown nofade field="customerCategory" defaultText={age} departureId={departureId} /></TableTitle>}
        { widths.nationality === 0 ? null : <TableTitle my={-2} width={widths.nationality} title={nationalityDescription}><MySelectAllDropdown nofade field="nationality" defaultText={nationality} searchable /></TableTitle>}
        { widths.cabin === 0 ? null : <PaddedTableTitle my={-2} title={hideCabinLabel ? '' : cabinDescription} width={(widths.cabin)}>{hideCabinLabel ? '' : cabin}</PaddedTableTitle>}
        <TableTitleRight my={-2} width={(widths.expand)} title={expandAllDescription}><StyledExpandAllButton /></TableTitleRight>
      </TableHeader>
    )}
  />
));
GuestListTableHeader.propTypes = {
  widths: PropTypes.object,
};

const ActiveOrInactiveRow = styled(Row)`
    display: ${ifProp('active', 'block', 'none')};
    margin: 0 !important;
`;

ActiveOrInactiveRow.propTypes = {
  active: PropTypes.bool,
};

const DepartureGuestList = pure(({ departure, active, widths, onManageAll, ...props }) => {
  const guests = getGuests(departure); // eslint-disable-line
  const vehicles = getVehicles(departure); // eslint-disable-line
  const hasVehicles = vehicles && vehicles.length > 0;
  const hasCabins = getCabins(departure).length > 0;
  const hideCabinLabel = departure.collectIcePhoneNumber && !hasCabins;
  return (
    <T
      text={[
        AppText.VehicleType,
        AppText.RegistrationNumber,
        AppText.Trailer,
      ]}
      render={([vehicleType, registrationNumber, trailer]) => (
        <ActiveOrInactiveRow active={active} {...props}>
          <ListHolder px={0} my={0} >
            <GuestListHeading onManageAll={onManageAll} departure={departure} hideCabinButton={!hasCabins} />
            <GuestListTableHeader widths={widths} departureId={departure.id} hideCabinLabel={hideCabinLabel} />
            {guests.map((id, index) => <GuestListEntry key={id} id={id} departureId={departure.id} number={index + 1} widths={widths} />)}
          </ListHolder>
          {hasVehicles && (
            <ListHolder px={0}>
              <TableHeader align="center" py={0}>
                <Flex align="center" width={1}>
                  <Box width={widths.number} />
                  <PaddedTableTitle px={1} width={widths.vehicleType}>{vehicleType}</PaddedTableTitle>
                  <PaddedTableTitle px={1} width={widths.registrationNumber}>{registrationNumber}</PaddedTableTitle>
                  <PaddedTableTitle px={1} width={widths.trailer}>{trailer}</PaddedTableTitle>
                </Flex>
              </TableHeader>
              <div>
                {vehicles.map((id, index) => <Vehicle key={id} id={id} departureId={departure.id} number={index + 1} widths={widths} />)}
              </div>
            </ListHolder>
          )}
          {/* {hasCabins && <GuestListFooter onManageAll={onManageAll} />} */}
          <SelectCabinModal departureId={departure.id} />
        </ActiveOrInactiveRow>
      )}
    />
  );
});
DepartureGuestList.propTypes = {
  departure: PropTypes.object,
  active: PropTypes.bool,
  onManageAll: PropTypes.func,
  onManageGuest: PropTypes.func,
};
DepartureGuestList.defaultProps = {
  departure: {},
  active: false,
  onManageAll: () => {},
  onManageGuest: () => {},
};

export default DepartureGuestList;



// WEBPACK FOOTER //
// ./src/components/organisms/DepartureGuestList/index.js